import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../components/authentication/AuthContext';
import { toast } from "react-toastify";

const apiUrl = import.meta.env.VITE_API_URL;

export const StoreContext = createContext();

const StoreContextProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const { token } = useContext(AuthContext);
  const [products, setProducts] = useState({});
  const [cart, setCart] = useState([]);
  const [cartItems, setCartItems] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    productsPerPage: 15,
  });

const fetchCart = async () => {
  try {
    const isLoggedIn = !!token?.token;
    const headers = isLoggedIn ? { Authorization: `Bearer ${token.token}` } : {};
    const endpoint = isLoggedIn ? `${apiUrl}/api/cart` : `${apiUrl}/api/cart-guest`;

    const response = await axios.get(endpoint, { headers });

    console.log('Cart response:', response.data);
    if (response.status === 200) {
      const aggregatedCart = Object.values(
        response.data.reduce((acc, item) => {
          const key = item.product_id;
          if (acc[key]) {
            acc[key].quantity += item.quantity || 1;
          } else {
            acc[key] = { ...item, quantity: item.quantity || 1 };
          }
          return acc;
        }, {})
      );
      setCart(aggregatedCart);
      const cartItemsMap = aggregatedCart.reduce(
        (acc, item) => ({ ...acc, [item.product_id]: item }),
        {}
      );
      setCartItems(cartItemsMap);
      await fetchCartProductDetails(aggregatedCart);
    }
  } catch (error) {
    console.error('Error fetching cart:', error);
    if (error.response?.status === 401) {
      toast.error('Authentication failed. Please log in again.', { position: 'top-right' });
    }
  }
};


  // ... (other functions remain unchanged, included for completeness)

  const fetchCartProductDetails = async (cartItems) => {
    try {
      const promises = cartItems.map((item) =>
        axios.get(`${apiUrl}/api/product-${item.product_id}`)
      );
      const responses = await Promise.all(promises);
      const productsData = responses.map((response) => response.data);
      const productsMap = productsData.reduce(
        (acc, product) => ({ ...acc, [product.id]: product }),
        {}
      );
      setProducts(productsMap);
    } catch (error) {
      console.error("Error fetching cart product details:", error);
    }
  };

  const addToCart = async (product) => {
    try {
      const response = await axios.post(
        `${apiUrl}/api/add-cart`,
        {
          user_id: token?.user_id || null,
          product_id: product.id,
          quantity: product.quantity || 1,
        },
        {
          headers: token?.token ? { Authorization: `Bearer ${token.token}` } : {},
        }
      );

      if (response.status === 201) {
        await fetchCart();
        toast.success("Item added to Cart!");
      } else {
        toast.error("Failed to add item to Cart!");
      }
    } catch (error) {
      toast.error("Error adding item to Cart!");
    }
  };

  const updateCartQuantity = async (productId, newQuantity) => {
    try {
      const response = await axios.post(
        `${apiUrl}/api/update-cart-quantity/${productId}`,
        {
          user_id: token?.user_id || null,
          quantity: newQuantity,
        },
        {
          headers: token?.token ? { Authorization: `Bearer ${token.token}` } : {},
        }
      );

      if (response.status === 200) {
        await fetchCart();
        toast.success("Quantity updated!");
      } else {
        toast.error("Failed to update quantity!");
      }
    } catch (error) {
      toast.error("Error updating quantity!");
    }
  };

  const removeFromCart = async (product) => {
  try {
    let response;
    if (token && token.token) {
      // User Logged in
      response = await axios.post(
        `${apiUrl}/api/delete-cart/${product.id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token.token}` },
        }
      );
    } else {
      // Guest
      response = await axios.post(
        `${apiUrl}/api/delete-cart-guest/${product.id}`
      );
    }

    if (response.status === 200) {
      await fetchCart();
      toast.success("Item removed from Cart!");
    } else {
      toast.error("Failed to remove item from Cart!");
    }
  } catch (error) {
    toast.error("Error removing item from Cart!");
  }
};


  const getCartQuantity = () => {
    return cart.reduce((acc, item) => acc + item.quantity, 0);
  };

  const addToWishlist = async (product) => {
    if (!token?.token) {
      toast.error("Please log in to add to wishlist!");
      return;
    }
    try {
      const response = await axios.post(
        `${apiUrl}/api/add-wishlist`,
        {
          user_id: token.user_id,
          product_id: product.id,
        },
        {
          headers: { Authorization: `Bearer ${token.token}` },
        }
      );
      if (response.status === 201) {
        setWishlist([...wishlist, product]);
        toast.success("Item added to wishlist!");
      } else {
        toast.error("Failed to add item to wishlist!");
      }
    } catch (error) {
      toast.error("Error adding item to wishlist!");
    }
  };

  const removeFromWishlist = async (product) => {
    if (!token?.token) {
      toast.error("Please log in to manage wishlist!");
      return;
    }
    try {
      const response = await axios.post(
        `${apiUrl}/api/delete-wishlist/${product.id}`,
        {
          user_id: token.user_id,
          product_id: product.id,
        },
        {
          headers: { Authorization: `Bearer ${token.token}` },
        }
      );

      if (response.status === 200) {
        setWishlist(wishlist.filter((item) => item.id !== product.id));
        toast.success("Item removed from wishlist!");
      } else {
        toast.error("Failed to remove item from wishlist!");
      }
    } catch (error) {
      toast.error("Error removing item from wishlist!");
    }
  };

  const addToCartFromWishlist = async (product) => {
    if (!token?.token) {
      toast.error("Please log in to add to cart from wishlist!");
      return;
    }
    try {
      const response = await axios.post(
        `${apiUrl}/api/wishlistToCart`,
        {
          user_id: token.user_id,
          product_id: product.product_id,
        },
        {
          headers: { Authorization: `Bearer ${token.token}` },
        }
      );

      if (response.status === 201) {
        setWishlist(wishlist.filter((item) => item.product_id !== product.product_id));
        await fetchCart();
        toast.success("Product added to cart from wishlist!");
      } else {
        toast.error("Failed to add item to cart from wishlist!");
      }
    } catch (error) {
      toast.error("Error adding item to cart from wishlist!");
    }
  };

  const fetchProductDetails = async (wishlistItems) => {
    try {
      const promises = wishlistItems.map((item) =>
        axios.get(`${apiUrl}/api/product-${item.product_id}`)
      );
      const responses = await Promise.all(promises);
      const productsData = responses.map((response) => response.data);
      const productsMap = productsData.reduce(
        (acc, product) => ({ ...acc, [product.id]: product }),
        {}
      );
      setProducts((prev) => ({ ...prev, ...productsMap }));
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [token]);

  useEffect(() => {
    if (wishlist.length > 0) {
      fetchProductDetails(wishlist);
    }
  }, [wishlist]);

  return (
    <StoreContext.Provider
      value={{
        wishlist,
        addToWishlist,
        removeFromWishlist,
        products,
        addToCartFromWishlist,
        cart,
        addToCart,
        pagination,
        removeFromCart,
        updateCartQuantity,
        getCartQuantity,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

export default StoreContextProvider;